import { Stack } from "@mui/material";
import { toInteger } from "lodash";
import { useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { GqlFieldworkerAvailability, GqlFieldworkerAvailabilityStatus, GqlFieldworkerCalendarDayEvents } from "~/__generated__";
import GaiaCalendarView from "~/modules/app/components/GaiaCalendarView";
import GaiaHookFormConfirmCloseDialog from "~/modules/app/components/GaiaHookFormConfirmCloseDialog";
import GaiaHookFormSubmitButton from "~/modules/app/components/GaiaHookFormSubmitButton";
import GaiaPageComponent from "~/modules/app/components/GaiaPageComponent";

import { getCalendarConfig } from "../../../app/util/calendarUtil";
import useCurrentUser from "../../../auth/hooks/useCurrentUser";
import useFieldworkerGetCalendarDaysEvents from "../../api/useFieldworkerGetCalendarDaysEvents";
import useFieldworkerUpdateAvailability from "../../api/useFieldworkerUpdateAvailability";
import CalendarDayEvents from "../CalendarDayEvents";

function createTimePeriodFromParams(params: Record<string, string | undefined>) {
    const currentDate = new Date();
    const calendarYear = toInteger(params.year) || currentDate.getFullYear();
    const calendarMonth = (toInteger(params.month) || currentDate.getMonth() + 1) - 1;

    return [calendarYear, calendarMonth] as const;
}

type FieldworkerPlanningProps = {
    baseUrl: string;
    userId?: string;
};

type FieldworkerPlanningInnerProps = {
    baseUrl: string;
    calendarYear: number;
    calendarMonth: number;
    defaultValues: FieldworkerPlanningFormValues;
    loading: boolean;
    onSubmit: SubmitHandler<FieldworkerPlanningFormValues>;
};

function FieldworkerPlanningInner({
    baseUrl,
    calendarMonth,
    calendarYear,
    defaultValues,
    onSubmit,
}: FieldworkerPlanningInnerProps) {
    const { t } = useTranslation("fieldworker");

    const { control, handleSubmit } = useForm<FieldworkerPlanningFormValues>({
        defaultValues,
    });

    return (
        <GaiaPageComponent
            sx={{
                display: "flex",
                flexDirection: "column",
                maxWidth: "100%",
                marginBottom: 2,
            }}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <GaiaHookFormConfirmCloseDialog control={control} />
                <Stack rowGap={2}>
                    <GaiaCalendarView
                        year={calendarYear}
                        month={calendarMonth}
                        baseUrl={baseUrl}
                        renderDay={(_dayInPeriod, _isActive, dayIndex) => (
                            <CalendarDayEvents control={control} dayIndex={dayIndex} />
                        )}
                    />
                    <GaiaHookFormSubmitButton control={control}>{t("calendarDay.saveChanges")}</GaiaHookFormSubmitButton>
                </Stack>
            </form>
        </GaiaPageComponent>
    );
}

export default function FieldworkerPlanning({ baseUrl }: FieldworkerPlanningProps) {
    const params = useParams<{ userId: string }>();
    const user = useCurrentUser();
    const [calendarYear, calendarMonth] = createTimePeriodFromParams(useParams());

    const { firstMonday, lastSunday } = getCalendarConfig(calendarYear, calendarMonth);
    const userId = params.userId || user!.id;

    const { data: daysEvents } = useFieldworkerGetCalendarDaysEvents(userId, firstMonday, lastSunday);

    const defaultValues = useMemo(() => mapAvailabilitiesToFormValues(daysEvents ?? []), [daysEvents]);
    const defaultValuesKey = useMemo(() => JSON.stringify(defaultValues), [defaultValues]);

    const { fieldworkerUpdateAvailability, loading } = useFieldworkerUpdateAvailability();

    const handleSubmit: SubmitHandler<FieldworkerPlanningFormValues> = async data => {
        const availabilities = data.elements.flatMap(element => {
            return element.availabilities.map(x => ({
                availabilityId: x.availabilityId,
                date: element.date,
                isAvailable: x.isAvailable,
            }));
        });

        await fieldworkerUpdateAvailability(userId, availabilities);
    };

    return daysEvents !== undefined ? (
        <FieldworkerPlanningInner
            key={defaultValuesKey}
            baseUrl={baseUrl}
            calendarMonth={calendarMonth}
            calendarYear={calendarYear}
            defaultValues={defaultValues}
            loading={loading}
            onSubmit={handleSubmit}
        />
    ) : (
        <span />
    );
}

export interface FieldworkerPlanningFormValues {
    elements: Array<{
        date: string;
        availabilities: Array<{
            availabilityId: string;
            disabled: boolean;
            name: string;
            isAvailable: boolean;
        }>;
    }>;
}

type AvailabilityShape = Pick<GqlFieldworkerAvailability, "id" | "name" | "status">;

type InputShape = Pick<GqlFieldworkerCalendarDayEvents, "date"> & {
    availabilities: AvailabilityShape[];
};

function mapAvailabilitiesToFormValues(data: InputShape[]): FieldworkerPlanningFormValues {
    return {
        elements: data.map(element => ({
            date: element.date,
            availabilities: element.availabilities.map(x => ({
                availabilityId: x.id,
                disabled: x.status === GqlFieldworkerAvailabilityStatus.Scheduled,
                isAvailable: x.status !== GqlFieldworkerAvailabilityStatus.Unavailable,
                name: x.name,
            })),
        })),
    };
}
